/**
 * Minimal toast notification system.
 *
 * Usage:
 *   const { toast } = useToast();
 *   toast.success('Task completed');
 *   toast.error('Something went wrong');
 *
 * <Toaster /> renders the notifications — place it once near the app root.
 */

import React, { createContext, useCallback, useContext, useReducer } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ToastVariant = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
}

interface ToastState {
  toasts: Toast[];
}

type ToastAction =
  | { type: 'add'; toast: Toast }
  | { type: 'remove'; id: string };

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

function reducer(state: ToastState, action: ToastAction): ToastState {
  switch (action.type) {
    case 'add':
      // Cap at 4 visible toasts.
      return { toasts: [...state.toasts.slice(-3), action.toast] };
    case 'remove':
      return { toasts: state.toasts.filter((t) => t.id !== action.id) };
  }
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface ToastContextValue {
  dispatch: React.Dispatch<ToastAction>;
}

const ToastContext = createContext<ToastContextValue | null>(null);

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

let nextId = 0;

// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast() called outside <ToastProvider>');

  const show = useCallback(
    (message: string, variant: ToastVariant, durationMs = 3500) => {
      const id = String(++nextId);
      ctx.dispatch({ type: 'add', toast: { id, message, variant } });
      setTimeout(() => ctx.dispatch({ type: 'remove', id }), durationMs);
    },
    [ctx],
  );

  return {
    toast: {
      success: (msg: string) => show(msg, 'success'),
      error: (msg: string) => show(msg, 'error'),
      info: (msg: string) => show(msg, 'info'),
    },
  };
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function ToastProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [state, dispatch] = useReducer(reducer, { toasts: [] });

  return (
    <ToastContext.Provider value={{ dispatch }}>
      {children}
      <Toaster toasts={state.toasts} dispatch={dispatch} />
    </ToastContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Toaster (renders the toasts)
// ---------------------------------------------------------------------------

function Toaster({
  toasts,
  dispatch,
}: {
  toasts: Toast[];
  dispatch: React.Dispatch<ToastAction>;
}): React.ReactElement | null {
  if (toasts.length === 0) return null;
  return (
    <div className="toaster" role="log" aria-live="polite" aria-label="Notifications">
      {toasts.map((t) => (
        <div key={t.id} className={`toast toast-${t.variant}`} role="status">
          <span className="toast-message">{t.message}</span>
          <button
            className="toast-close"
            aria-label="Dismiss"
            onClick={() => dispatch({ type: 'remove', id: t.id })}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
